import React, { useState, useEffect, useRef } from "react";
import { Card, CardBody, Col, Button, Spinner, Container, Input, Modal, Nav, NavItem, NavLink, TabContent, TabPane, Badge } from "reactstrap";
import DataTable from "react-data-table-component";
import Breadcrumbs from "../../CommonElements/Breadcrumbs";
import { apiPost, mediaUrl } from "../../Utils";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Btn, H3 } from "../../AbstractElements";
import { useNavigate } from "react-router-dom";
import { Edit, Plus, Trash2, Eye, Truck, Droplet, FileText, Calendar } from 'react-feather';
import Swal from 'sweetalert2';
import { useAuth } from "../../Context/AuthContext";
import { useConfig } from "../../Context/ConfigContext";

export default function ViewFuelExpense() {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalRows, setTotalRows] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [refresh, setRefresh] = useState(0);
    const [searchTerm, setSearchTerm] = useState("");
    const [sortColumn, setSortColumn] = useState("");
    const [sortDirection, setSortDirection] = useState("");

    // Modal State
    const [viewModal, setViewModal] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const [selectedExpense, setSelectedExpense] = useState<any>(null);
    const [activeTab, setActiveTab] = useState('1');

    const prevSearch = useRef(searchTerm);
    const navigate = useNavigate();
    const { hasPermission } = useAuth();
    const { config } = useConfig();

    useEffect(() => {
        const isSearchChanged = prevSearch.current !== searchTerm;
        prevSearch.current = searchTerm;

        // Reset to page 1 when search changes
        if (isSearchChanged && currentPage !== 1) {
            setCurrentPage(1);
            return; // Exit early, let page change trigger next fetch
        }

        const delay = isSearchChanged ? 500 : 0;
        const timer = setTimeout(() => {
            fetchData(currentPage, perPage, searchTerm, sortColumn, sortDirection);
        }, delay);
        return () => clearTimeout(timer);
    }, [refresh, currentPage, perPage, searchTerm, sortColumn, sortDirection]);

    const fetchData = async (page: number, size: number, search: string = "", sortCol: string = "", sortDir: string = "") => {
        setLoading(true);
        try {
            const body: any = {
                page: page,
                per_page: size,
                search: search
            };

            if (sortCol) {
                body.sort_by = sortCol;
                body.sort_direction = sortDir;
            }

            const response: any = await apiPost("fuel_expenses/getall", body);
            if (response && response.data) {
                setData(response.data);
                setTotalRows(response.total);
            } else {
                setData(response);
                setTotalRows(response.length);
            }
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleSort = (column: any, sortDir: string) => {
        if (column.sortField) {
            setSortColumn(column.sortField);
            setSortDirection(sortDir);
            setCurrentPage(1);
        }
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const handlePerRowsChange = async (newPerPage: number, page: number) => {
        setPerPage(newPerPage);
        setCurrentPage(page);
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
    };

    const handleAdd = () => {
        navigate(`${process.env.PUBLIC_URL}/add_fuel_expense`);
    };

    const handleEdit = (id: number) => {
        navigate(`${process.env.PUBLIC_URL}/edit_fuel_expense`, { state: { id: id } });
    };

    const handleDelete = async (id: number) => {
        Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await apiPost("fuel_expenses/delete", { id: id });
                    toast.success("Record deleted successfully", { autoClose: 1000 });
                    setRefresh(prev => prev + 1);
                } catch (error) {
                    toast.error("Failed to delete record", { autoClose: 1000 });
                }
            }
        });
    };

    // Modal Helpers
    const toggleViewModal = () => {
        if (viewModal) {
            handleClose();
        } else {
            setViewModal(true);
        }
    };

    const handleClose = () => {
        setIsClosing(true);
        setTimeout(() => {
            setViewModal(false);
            setIsClosing(false);
        }, 550);
    };

    const toggleTab = (tab: string) => {
        if (activeTab !== tab) setActiveTab(tab);
    }
    const handleView = (row: any) => {
        setSelectedExpense(row);
        setActiveTab('1');
        setViewModal(true);
    };


    const columns = React.useMemo(() => [
        {
            name: <div>Sl. No</div>,
            selector: (row: any, index?: number) => (currentPage - 1) * perPage + (index || 0) + 1,
            width: "80px",
        },
        {
            name: <div>Reg No</div>,
            selector: (row: any) => row.vehicle?.registration_number || 'N/A',
            sortable: true,
            sortField: "registration_number",
            wrap: true,
            width: "150px",
            cell: (row: any) => (
                <span className="fw-bold text-primary">
                    {row.vehicle?.registration_number || 'N/A'}
                    {row.vehicle?.deleted_at && (
                        <span className="text-danger ms-1">(Deleted)</span>
                    )}
                </span>
            )
        },
        {
            name: <div>Branch</div>,
            selector: (row: any) => row.branch?.branch_name || 'N/A',
            sortable: true,
            sortField: "branch_name",
            wrap: true,
            cell: (row: any) => (
                <span>
                    {row.branch?.branch_name || 'N/A'}
                    {row.branch?.deleted_at && (
                        <span className="text-danger ms-1">(Deleted)</span>
                    )}
                </span>
            )
        },
        {
            name: <div>Fuel Date</div>,
            selector: (row: any) => row.fuel_date_time,
            sortable: true,
            sortField: "fuel_date_time",
            wrap: true,
            cell: (row: any) => row.fuel_date_time ? new Date(row.fuel_date_time).toLocaleDateString('en-GB') : "N/A"
        },
        {
            name: <div>Fuel Type</div>,
            selector: (row: any) => row.fuel_type,
            sortable: true,
            sortField: "fuel_type",
            wrap: true,
        },
        {
            name: <div>Amount ({config.currency})</div>,
            selector: (row: any) => row.amount,
            sortable: true,
            sortField: "amount",
            cell: (row: any) => <span className="text-success fw-bold">{row.amount}</span>
        },
        {
            name: <div>Actions</div>,
            cell: (row: any) => (
                <div className="d-flex justify-content-between gap-2">
                    {hasPermission('fuel-expense.view') && (
                        <Button size="sm" color="primary" onClick={() => handleView(row)} title="View Details">
                            <Eye size={14} />
                        </Button>
                    )}
                    {hasPermission('fuel-expense.edit') && (
                        <Button size="sm" color="info" onClick={() => handleEdit(row.id)} title="Edit">
                            <Edit size={14} />
                        </Button>
                    )}
                    {hasPermission('fuel-expense.delete') && (
                        <Button size="sm" color="danger" onClick={() => handleDelete(row.id)} title="Delete">
                            <Trash2 size={14} />
                        </Button>
                    )}
                </div>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            width: "160px"
        },
    ], [currentPage, perPage]);

    const customStyles = {
        headRow: {
            style: {
                backgroundColor: '#e0f2f1',
                color: '#00695c',
                minHeight: '50px',
                borderBottom: '1px solid #e0e0e0',
                fontWeight: 'bold',
                fontSize: '14px',
            },
        },
        headCells: {
            style: {
                fontSize: '15px',
                fontWeight: '600',
                paddingLeft: '16px',
                paddingRight: '16px',
            },
        },
        rows: {
            style: {
                minHeight: '50px',
                '&:not(:last-of-type)': {
                    borderBottomStyle: 'solid',
                    borderBottomWidth: '1px',
                    borderBottomColor: '#e0e0e0',
                },
            },
        },
    };

    return (
        <div className='page-body'>
            <Breadcrumbs pageTitle="Fuel Expense" parent="Operations" title="Fuel Expense" />
            <Container fluid>
                <Col xl={12} className="mb-lg-3">
                    <Card>
                        <CardBody>
                            <Col xs={12} className="mt-2 d-flex justify-content-between align-items-center mb-3">
                                <H3 className="text-primary">Fuel Expense Overview</H3>
                                {hasPermission('fuel-expense.add') && (
                                    <Btn color="primary" onClick={handleAdd}>
                                        <Plus size={16} /> Add Expense
                                    </Btn>
                                )}
                            </Col>
                            <>
                                <Col xs={12} className="mb-3 d-flex justify-content-end">
                                    <Input type="text" placeholder="Search..." value={searchTerm} onChange={handleSearchChange} style={{ maxWidth: "300px" }} />
                                </Col>
                                <div className="table-responsive">
                                    <DataTable
                                        data={data}
                                        columns={columns}
                                        pagination
                                        paginationServer
                                        paginationTotalRows={totalRows}
                                        onChangePage={handlePageChange}
                                        onChangeRowsPerPage={handlePerRowsChange}
                                        onSort={handleSort}
                                        sortServer
                                        progressPending={loading}
                                        progressComponent={<div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: '50vh' }}><Spinner color="primary" /></div>}
                                        highlightOnHover
                                        customStyles={customStyles}
                                    />
                                </div>
                            </>

                        </CardBody>
                    </Card>
                </Col>
            </Container>

            {/* View Fuel Expense Modal */}
            <Modal isOpen={viewModal} toggle={toggleViewModal} size="lg" centered contentClassName={isClosing ? "animate-3d-slit-out" : "animate-3d-slit"} fade={false}>
                {selectedExpense && (
                    <div style={{
                        background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',// Orange gradient for fuel
                        padding: '1.5rem',
                        position: 'relative', width: '100%'
                    }}>
                        <button
                            className="btn-close btn-close-white"
                            type="button"
                            onClick={handleClose}
                            style={{ position: 'absolute', top: '1rem', right: '1rem', opacity: 0.9 }}
                        ></button>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
                            <div style={{
                                width: '60px',
                                height: '60px',
                                borderRadius: '50%',
                                backgroundColor: 'rgba(255,255,255,0.2)',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                flexShrink: 0
                            }}>
                                <Droplet size={30} color="white" />
                            </div>
                            <div style={{ flex: 1 }}>
                                <h3 style={{
                                    color: 'white',
                                    fontWeight: 700,
                                    fontSize: '1.5rem',
                                    margin: 0,
                                    marginBottom: '0.25rem'
                                }}>
                                    {selectedExpense.vehicle?.registration_number}
                                </h3>
                                <div style={{ display: 'flex', gap: '1rem', color: 'rgba(255,255,255,0.85)', fontSize: '0.9rem' }}>
                                    <span style={{ fontWeight: 600 }}>{selectedExpense.vehicle?.brand} {selectedExpense.vehicle?.model}</span>
                                    <span>•</span>
                                    <span>{new Date(selectedExpense.fuel_date_time).toLocaleDateString('en-GB')}</span>
                                </div>
                            </div>
                            <div>
                                <span style={{
                                    backgroundColor: 'rgba(255,255,255,0.2)',
                                    color: 'white',
                                    padding: '0.5rem 1rem',
                                    margin: '0.5rem 1rem',
                                    borderRadius: '20px',
                                    fontSize: '1.1rem',
                                    fontWeight: 700,
                                    border: '1px solid rgba(255,255,255,0.2)',
                                    backdropFilter: 'blur(4px)'
                                }}>
                                    {selectedExpense.amount} {config.currency}
                                </span>
                            </div>
                        </div>
                    </div>
                )}

                <div className="modal-body p-0">
                    <Nav tabs className="nav-primary" style={{ padding: '0 1.5rem', marginTop: '1rem', borderBottom: '1px solid #dee2e6' }}>
                        <NavItem>
                            <NavLink
                                className={activeTab === '1' ? 'active' : ''}
                                onClick={() => toggleTab('1')}
                                style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 20px' }}
                            >
                                <Truck size={16} /> Expense Details
                            </NavLink>
                        </NavItem>
                        <NavItem>
                            <NavLink
                                className={activeTab === '2' ? 'active' : ''}
                                onClick={() => toggleTab('2')}
                                style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 20px' }}
                            >
                                <FileText size={16} /> Slip & Remarks
                            </NavLink>
                        </NavItem>
                    </Nav>

                    <div className="p-4" style={{ backgroundColor: '#f8f9fa', minHeight: '300px' }}>
                        {selectedExpense && (
                            <TabContent activeTab={activeTab}>
                                <TabPane tabId="1">
                                    <div className="bg-white p-4 rounded shadow-sm">
                                        <div className="row g-4">
                                            <div className="col-md-6">
                                                <div className="mb-3">
                                                    <label className="text-muted small fw-bold text-uppercase">Branch</label>
                                                    <div className="fw-semibold">{selectedExpense.branch?.branch_name || 'N/A'}</div>
                                                </div>
                                                <div className="mb-3">
                                                    <label className="text-muted small fw-bold text-uppercase">Vehicle</label>
                                                    <div className="fw-semibold">
                                                        {selectedExpense.vehicle?.registration_number} ({selectedExpense.vehicle?.brand} {selectedExpense.vehicle?.model})
                                                    </div>
                                                </div>
                                                <div className="mb-3">
                                                    <label className="text-muted small fw-bold text-uppercase">Fuel Type</label>
                                                    <div className="fw-semibold text-primary">{selectedExpense.fuel_type}</div>
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="mb-3">
                                                    <label className="text-muted small fw-bold text-uppercase">Fuel Date & Time</label>
                                                    <div className="fw-semibold">{selectedExpense.fuel_date_time ? new Date(selectedExpense.fuel_date_time).toLocaleString() : 'N/A'}</div>
                                                </div>
                                                <div className="mb-3">
                                                    <label className="text-muted small fw-bold text-uppercase">Odometer Reading</label>
                                                    <div className="fw-semibold">{selectedExpense.odometer_reading} km</div>
                                                </div>
                                                <div className="mb-3">
                                                    <label className="text-muted small fw-bold text-uppercase">Amount ({config.currency})</label>
                                                    <div className="fw-bold fs-5 text-success">{selectedExpense.amount}</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </TabPane>

                                <TabPane tabId="2">
                                    <div className="bg-white p-4 rounded shadow-sm">
                                        <div className="row g-4">
                                            <div className="col-12">
                                                <label className="text-muted small fw-bold text-uppercase mb-2">Payment Slip</label>
                                                <div className="p-3 border rounded d-flex justify-content-between align-items-center bg-light">
                                                    <div className="d-flex align-items-center">
                                                        <FileText size={20} className="text-muted me-2" />
                                                        <div>

                                                            {selectedExpense.payment_slip && (
                                                                <small className="text-muted">Payment slip document</small>
                                                            )}
                                                        </div>
                                                    </div>
                                                    {selectedExpense.payment_slip && (
                                                        <a
                                                            href={`${mediaUrl}${selectedExpense.payment_slip}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="btn btn-sm btn-primary"
                                                        >
                                                            View Slip
                                                        </a>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="col-12">
                                                <label className="text-muted small fw-bold text-uppercase">Remarks</label>
                                                <div className="p-3 bg-light rounded text-dark" style={{ minHeight: '80px' }}>
                                                    {selectedExpense.remarks || <em className="text-muted">No remarks provided.</em>}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </TabPane>
                            </TabContent>
                        )}
                    </div>
                </div>
            </Modal>
            <ToastContainer />
        </div>
    );
}
